import { useId, useState } from 'react';
import {
  Box,
  Collapse,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Typography,
  Tooltip,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import type {
  AiWalletPeriodView,
  ResellerAiFeatureUsageRow,
  ResellerAiPersonUsageRow,
} from '@/api/generated';
import { ApiErrorState } from '@/components/ApiErrorState';
import { moneyMicroIn } from '@/components/moneyIn';
import { PeriodPicker } from '@/components/PeriodPicker';
import { ReportScope } from '@/components/ReportScope';
import { enumParam, useUrlState } from '@/components/useUrlState';
import type { PeriodRange } from '@/i18n/periods';
import { useLocale } from '@/i18n/useLocale';
import { MoneyCell, NumericCell } from '@/components/cells';
import { Icon } from '@/theme';
import { featureLabel, formatCountOrDash } from './settingsLabels';
import { useAiUsage } from './useAiUsage';

/** Name, requests, failed, and charged to wallet; "At platform price" and wallet balance add one each. */
const BASE_COLUMNS = 4;

/** Period figures, then the balance now. Every field is required and rendered as sent. */
const WALLET_FIGURES: ReadonlyArray<{
  key: string;
  field: Exclude<keyof AiWalletPeriodView, 'currency'>;
  now?: boolean;
}> = [
  { key: 'settings.ai.wallet.purchases', field: 'purchasesMinor' },
  { key: 'settings.ai.wallet.recharges', field: 'rechargesMinor' },
  { key: 'settings.ai.wallet.topUps', field: 'topUpsMinor' },
  { key: 'settings.ai.wallet.monthlyCredits', field: 'monthlyCreditsMinor' },
  { key: 'settings.ai.wallet.adminCredits', field: 'adminCreditsMinor' },
  { key: 'settings.ai.wallet.usage', field: 'usageMinor' },
  { key: 'settings.ai.wallet.balance', field: 'balanceMinor', now: true },
];

/** The view in the URL (`?tab=person`, TMM-B6); by feature is the omitted default. */
const TAB_SCHEMA = { tab: enumParam(['feature', 'person'], 'feature') };

/** A table shows "At platform price" only when one of its rows carries it (TMMAPI-0101). */
function hasAtPlatformPrice(
  rows: Array<{ atOurPriceMicro?: number; atOurPricePartial?: boolean }>,
): boolean {
  return rows.some(
    (row) => typeof row.atOurPriceMicro === 'number' || row.atOurPricePartial === true,
  );
}

function AtPlatformPriceHead() {
  const { t } = useTranslation();
  return <TableCell align="right">{t('settings.ai.col.atPlatformPrice')}</TableCell>;
}

/** Our AI price before the reseller's markup; the dash when the row doesn't carry it. */
function AtPlatformPriceCell({
  micro,
  partial,
  currency,
  locale,
}: {
  micro: number | undefined;
  partial?: boolean | undefined;
  currency: string;
  locale: string;
}) {
  const { t } = useTranslation();
  const dash = t('settings.unavailable');
  return (
    <TableCell align="right">
      {partial ? (
        <Tooltip title={t('settings.ai.partialHelp')} describeChild>
          <Box component="span" tabIndex={0}>
            {t('settings.ai.partial')}
          </Box>
        </Tooltip>
      ) : typeof micro === 'number' ? (
        moneyMicroIn(micro, currency, locale, dash)
      ) : (
        dash
      )}
    </TableCell>
  );
}

function FeatureRows({
  rows,
  showAtPrice,
  locale,
}: {
  rows: ResellerAiFeatureUsageRow[];
  showAtPrice: boolean;
  locale: string;
}) {
  const { t } = useTranslation();
  return (
    <>
      {rows.map((row, index) => (
        <TableRow key={row.feature ?? index}>
          <TableCell component="th" scope="row">
            {featureLabel(row.feature)}
          </TableCell>
          <NumericCell value={row.requests} />
          <NumericCell value={row.failed} />
          {showAtPrice ? (
            <AtPlatformPriceCell
              micro={row.atOurPriceMicro}
              partial={row.atOurPricePartial}
              currency={row.currency}
              locale={locale}
            />
          ) : null}
          <MoneyCell minor={row.estimatedChargeMinor} currency={row.currency} />
        </TableRow>
      ))}
      {rows.length === 0 ? (
        <TableRow>
          <TableCell colSpan={BASE_COLUMNS + (showAtPrice ? 1 : 0)}>
            {t('settings.ai.emptyFeatures')}
          </TableCell>
        </TableRow>
      ) : null}
    </>
  );
}

/** The person's AI wallet over this period, or the no-wallet sentence. Read-only. */
function WalletActivity({
  wallet,
  titleId,
}: {
  wallet: AiWalletPeriodView | undefined;
  titleId: string;
}) {
  const { t } = useTranslation();
  if (wallet === undefined) {
    return (
      <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
        {t('settings.ai.wallet.none')}
      </Typography>
    );
  }
  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle2" component="p" id={titleId}>
        {t('settings.ai.wallet.title')}
      </Typography>
      <Typography variant="caption" component="p" sx={{ color: 'text.secondary' }}>
        {t('settings.ai.wallet.caption')}
      </Typography>
      <Table size="small" aria-labelledby={titleId} sx={{ mt: 1, maxWidth: 480 }}>
        <TableHead>
          <TableRow>
            <TableCell>{t('settings.ai.wallet.col.item')}</TableCell>
            <TableCell align="right">{t('settings.ai.wallet.col.amount')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {WALLET_FIGURES.map((figure) => (
            <TableRow
              key={figure.field}
              sx={figure.now ? { '& > *': { fontWeight: 600, color: 'text.primary' } } : undefined}
            >
              <TableCell component="th" scope="row">
                {t(figure.key)}
              </TableCell>
              <MoneyCell minor={wallet[figure.field]} currency={wallet.currency} />
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

function PersonRows({
  people,
  showAtPrice,
  showWallet,
  locale,
}: {
  people: ResellerAiPersonUsageRow[];
  showAtPrice: boolean;
  showWallet: boolean;
  locale: string;
}) {
  const { t } = useTranslation();
  // Which person's features are shown: local, not URL state (TMMRSL-0072).
  const [expanded, setExpanded] = useState<number | undefined>(undefined);
  const baseId = useId();

  return (
    <>
      {people.map((person, index) => {
        const id = person.userId;
        const open = expanded === id;
        const features = person.features;
        const featuresAtPrice = hasAtPlatformPrice(features);
        const name = person.displayName ?? person.email ?? t('settings.unavailable');
        const nameId = `${baseId}-name-${index}`;
        const regionId = `${baseId}-features-${index}`;
        const toggle = () => {
          setExpanded((prev) => (prev === id ? undefined : id));
        };
        return (
          <TableRow
            key={id}
            hover
            // A mouse convenience: the disclosure button is the control.
            onClick={toggle}
            sx={{ cursor: 'pointer' }}
          >
            <TableCell component="th" scope="row">
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                <IconButton
                  size="small"
                  aria-expanded={open}
                  aria-controls={regionId}
                  aria-label={t('common.showDetailsFor', { name })}
                  onClick={(event) => {
                    event.stopPropagation();
                    toggle();
                  }}
                >
                  <Icon name={open ? 'expand_less' : 'expand_more'} />
                </IconButton>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" id={nameId}>
                    {person.displayName ?? t('settings.unavailable')}
                  </Typography>
                  {person.email ? (
                    <Typography variant="caption" component="p" sx={{ color: 'text.secondary' }}>
                      {person.email}
                    </Typography>
                  ) : null}
                </Box>
              </Box>
              <Collapse in={open} timeout="auto" unmountOnExit id={regionId}>
                <Table size="small" sx={{ mt: 1 }} aria-labelledby={nameId}>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('settings.ai.col.feature')}</TableCell>
                      <TableCell align="right">{t('settings.ai.col.requests')}</TableCell>
                      <TableCell align="right">{t('settings.ai.col.failed')}</TableCell>
                      {featuresAtPrice ? <AtPlatformPriceHead /> : null}
                      <TableCell align="right">{t('settings.ai.col.charge')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <FeatureRows rows={features} showAtPrice={featuresAtPrice} locale={locale} />
                  </TableBody>
                </Table>
                <WalletActivity wallet={person.wallet} titleId={`${baseId}-wallet-${index}`} />
              </Collapse>
            </TableCell>
            <NumericCell value={person.requests} />
            <NumericCell value={person.failed} />
            {showAtPrice ? (
              <AtPlatformPriceCell
                micro={person.atOurPriceMicro}
                currency={person.currency}
                locale={locale}
              />
            ) : null}
            <MoneyCell
              minor={person.estimatedChargeMinor}
              currency={person.currency}
            />
            {showWallet ? (
              person.wallet !== undefined ? (
                <MoneyCell minor={person.wallet.balanceMinor} currency={person.wallet.currency} />
              ) : (
                <TableCell align="right">{t('settings.ai.wallet.noneShort')}</TableCell>
              )
            ) : null}
          </TableRow>
        );
      })}
      {people.length === 0 ? (
        <TableRow>
          <TableCell colSpan={BASE_COLUMNS + (showAtPrice ? 1 : 0) + (showWallet ? 1 : 0)}>
            {t('settings.ai.emptyPeople')}
          </TableCell>
        </TableRow>
      ) : null}
    </>
  );
}

export function AiUsageTable() {
  const { t } = useTranslation();
  const locale = useLocale();
  const [range, setRange] = useState<PeriodRange>({});
  const url = useUrlState(TAB_SCHEMA);
  const tab = url.values.tab === 'person' ? 1 : 0;
  const tabsId = useId();
  const tabId = (index: number) => `${tabsId}-tab-${index}`;
  const panelId = `${tabsId}-panel`;
  const { status, data, error, reload } = useAiUsage(range);
  const features: ResellerAiFeatureUsageRow[] = data?.features ?? [];
  const people: ResellerAiPersonUsageRow[] = data?.people ?? [];
  const showAtPrice = hasAtPlatformPrice(tab === 0 ? features : people);
  const showWallet = people.some((person) => person.wallet !== undefined);

  return (
    <Box>
      <Stack spacing={1.5}>
        <PeriodPicker screen="aiUsage" scope="tenant" onChange={setRange} />
        {status === 'loading' && !data ? (
          <Box role="status" aria-label={t('common.loading')}>
            <Skeleton variant="rounded" height={180} />
          </Box>
        ) : null}
        {status === 'error' ? <ApiErrorState error={error} onRetry={reload} /> : null}
        {data ? (
          <>
            <ReportScope from={data.from} to={data.to} />
            <Typography variant="body2">
              {t('settings.ai.summary', {
                requests: t('settings.ai.requests', {
                  count: data.requests,
                  formatted: formatCountOrDash(data.requests, locale),
                }),
                failed: t('settings.ai.failedCount', {
                  count: data.failed,
                  formatted: formatCountOrDash(data.failed, locale),
                }),
              })}
            </Typography>
            <Tabs
              value={tab}
              onChange={(_, value: number) => url.set({ tab: value === 1 ? 'person' : 'feature' })}
              aria-label={t('settings.ai.tabsLabel')}
            >
              <Tab
                label={t('settings.ai.tab.feature')}
                id={tabId(0)}
                aria-controls={panelId}
              />
              <Tab label={t('settings.ai.tab.person')} id={tabId(1)} aria-controls={panelId} />
            </Tabs>
            <Paper
              variant="outlined"
              role="tabpanel"
              id={panelId}
              aria-labelledby={tabId(tab)}
              sx={{ overflow: 'auto' }}
            >
              <Table size="small" aria-labelledby={tabId(tab)}>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      {tab === 0 ? t('settings.ai.col.feature') : t('settings.ai.col.person')}
                    </TableCell>
                    <TableCell align="right">{t('settings.ai.col.requests')}</TableCell>
                    <TableCell align="right">{t('settings.ai.col.failed')}</TableCell>
                    {showAtPrice ? <AtPlatformPriceHead /> : null}
                    <TableCell align="right">{t('settings.ai.col.charge')}</TableCell>
                    {tab === 1 && showWallet ? (
                      <TableCell align="right">{t('settings.ai.col.walletBalance')}</TableCell>
                    ) : null}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tab === 0 ? (
                    <FeatureRows rows={features} showAtPrice={showAtPrice} locale={locale} />
                  ) : (
                    <PersonRows
                      people={people}
                      showAtPrice={showAtPrice}
                      showWallet={showWallet}
                      locale={locale}
                    />
                  )}
                </TableBody>
              </Table>
            </Paper>
          </>
        ) : null}
      </Stack>
    </Box>
  );
}
